import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { FormsModule } from '@angular/forms';
import { Highlight } from 'ngx-highlightjs';
import { DataService } from '../../services/data.service';
import { ProjectGet } from '../../models/project/project-get.model';
import { DataSourceGet } from '../../models/datasource';

export interface StartInstructionsDialogData {
  project: ProjectGet;
  dataSource: DataSourceGet | null;
}

@Component({
  selector: 'app-start-project-instructions-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatTabsModule, MatButtonToggleModule, FormsModule, Highlight],
  templateUrl: './start-project-instructions-dialog.component.html',
  styleUrls: ['../start-project-dialog/start-project-dialog.component.scss']
})
export class StartProjectInstructionsDialogComponent implements OnInit {
  endpoint = '';
  statusEndpoint = '';
  resultsEndpoint = '';
  token = '';
  placeholderToken = '••••••••-••••-••••-••••-••••••••••••';
  tokenJustCopied = false;
  endpointJustCopied = false;
  codeJustCopied = false;
  isRegeneratingToken = false;
  tokenRegenerationError: string | null = null;
  selectedApiCall: 'start' | 'status' | 'results' = 'start';

  constructor(
    public dialogRef: MatDialogRef<StartProjectInstructionsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: StartInstructionsDialogData,
    private dataService: DataService
  ) {}

  ngOnInit(): void {
    const baseUrl = this.dataService.getBaseUrl();
    if (this.data?.project && this.data?.dataSource) {
      this.endpoint = `${baseUrl}/Project/clean_data/${this.data.project.projectId}/${this.data.dataSource.dataSourceId}`;
      this.statusEndpoint = `${baseUrl}/LongRunningAction/status/{ACTION_TOKEN}`;
      this.resultsEndpoint = `${baseUrl}/LongRunningAction/result/{ACTION_TOKEN}`;

      this.token = this.data.project.isTokenPresent ? this.placeholderToken : this.placeholderToken;
    }
  }

  copyToClipboard(text: string, type: 'endpoint' | 'token' | 'code'): void {
    navigator.clipboard.writeText(text).then(() => {
      if (type === 'endpoint') {
        this.endpointJustCopied = true;
        setTimeout(() => this.endpointJustCopied = false, 2000);
      } else if (type === 'token') {
        this.tokenJustCopied = true;
        setTimeout(() => this.tokenJustCopied = false, 2000);
      } else {
        this.codeJustCopied = true;
        setTimeout(() => this.codeJustCopied = false, 2000);
      }
    });
  }

  onClose(): void {
    this.dialogRef.close();
  }

  isTokenAvailable(): boolean {
    return this.token !== this.placeholderToken && this.token !== '';
  }

  regenerateToken(): void {
    if (!this.data.project?.projectId) {
      console.error('No project ID available for token regeneration');
      return;
    }

    this.isRegeneratingToken = true;
    this.tokenRegenerationError = null;

    this.dataService.getProjectToken(this.data.project.projectId).subscribe({
      next: (newToken) => {
        this.token = newToken;
        this.isRegeneratingToken = false;
        console.log('Project token regenerated successfully:', newToken);
        
        // Automatically copy the new token to clipboard
        this.copyToClipboard(newToken, 'token');
      },
      error: (error) => {
        this.isRegeneratingToken = false;
        this.tokenRegenerationError = error.message || 'Failed to regenerate token';
        console.error('Error regenerating token:', error);
      }
    });
  }

  // NOTE: For file sources the start call is POST but no request body is required;
  // the server uses the previously uploaded file for processing.
  getCurlCommand(): string {
    if (!this.endpoint || !this.token) return '# Endpoint or token not available';
    return `curl -X POST "${this.endpoint}" \\
  -H "Authorization: Bearer ${this.token}"`;
  }

  getCSharpCode(): string {
    if (!this.endpoint || !this.token) return '// Endpoint or token not available';
    return `using System;
using System.Net.Http;
using System.Threading.Tasks;

class ApiClient
{
    private static readonly string ApiEndpoint = "${this.endpoint}";
    private static readonly string BearerToken = "${this.token}";
    
    public static async Task<string> StartWorkflowAsync()
    {
        using var client = new HttpClient();
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {BearerToken}");
        
        var response = await client.PostAsync(ApiEndpoint, null);
        response.EnsureSuccessStatusCode();
        return await response.Content.ReadAsStringAsync();
    }
    
    static async Task Main(string[] args)
    {
        var result = await StartWorkflowAsync();
        Console.WriteLine(result);
    }
}`;
  }

  getVBNetCode(): string {
    if (!this.endpoint || !this.token) return "'Endpoint or token not available";
    return `Imports System
Imports System.Net.Http
Imports System.Threading.Tasks

Module ApiClient
    Private Const ApiEndpoint As String = "${this.endpoint}"
    Private Const BearerToken As String = "${this.token}"
    
    Public Async Function StartWorkflowAsync() As Task(Of String)
        Using client As New HttpClient()
            client.DefaultRequestHeaders.Add("Authorization", $"Bearer {BearerToken}")
            
            Dim response = Await client.PostAsync(ApiEndpoint, Nothing)
            response.EnsureSuccessStatusCode()
            Return Await response.Content.ReadAsStringAsync()
        End Using
    End Function
    
    Async Function Main() As Task
        Dim result = Await StartWorkflowAsync()
        Console.WriteLine(result)
    End Function
End Module`;
  }

  getPythonCode(): string {
    if (!this.endpoint || !this.token) return '# Endpoint or token not available';
    return `import requests
from typing import Dict

API_ENDPOINT = "${this.endpoint}"
BEARER_TOKEN = "${this.token}"

def start_workflow() -> Dict:
    """
    Start the workflow using the previously uploaded file.
    No request body is required.
    """
    headers = {
        "Authorization": f"Bearer {BEARER_TOKEN}"
    }
    
    response = requests.post(API_ENDPOINT, headers=headers)
    response.raise_for_status()
    return response.json()

if __name__ == "__main__":
    result = start_workflow()
    print(result)`;
  }

  getJavaCode(): string {
    if (!this.endpoint || !this.token) return '// Endpoint or token not available';
    return `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;

public class ApiClient {
    private static final String API_ENDPOINT = "${this.endpoint}";
    private static final String BEARER_TOKEN = "${this.token}";
    private static final HttpClient client = HttpClient.newHttpClient();
    
    public static String startWorkflow() throws Exception {
        HttpRequest request = HttpRequest.newBuilder()
            .uri(URI.create(API_ENDPOINT))
            .header("Authorization", "Bearer " + BEARER_TOKEN)
            .POST(HttpRequest.BodyPublishers.noBody())
            .build();
        
        HttpResponse<String> response = client.send(request, 
            HttpResponse.BodyHandlers.ofString());
        
        if (response.statusCode() != 200) {
            throw new RuntimeException("API returned status: " + response.statusCode());
        }
        
        return response.body();
    }
    
    public static void main(String[] args) throws Exception {
        String result = startWorkflow();
        System.out.println(result);
    }
}`;
  }

  getStatusCurlCommand(): string {
    if (!this.statusEndpoint || !this.token) return '# Endpoint or token not available';
    return `# Replace {ACTION_TOKEN} with the token returned from the start call
# Poll every few seconds until status is 2 (Complete) or 3 (Failed)
curl -X GET "${this.statusEndpoint}" \\
  -H "Authorization: Bearer ${this.token}"`;
  }

  getStatusCSharpCode(): string {
    if (!this.statusEndpoint || !this.token) return '// Endpoint or token not available';
    return `using System.Net.Http;
using System.Text.Json;

class StatusChecker
{
    private static readonly string BearerToken = "${this.token}";
    private static readonly string StatusUrl = "${this.statusEndpoint}";

    // Replace actionToken with the value returned from the start call
    // Status codes: 0=Pending, 1=Running, 2=Complete, 3=Failed
    public static async Task<JsonElement> PollUntilCompleteAsync(string actionToken)
    {
        using var client = new HttpClient();
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {BearerToken}");

        while (true)
        {
            var url = StatusUrl.Replace("{ACTION_TOKEN}", actionToken);
            var response = await client.GetAsync(url);
            response.EnsureSuccessStatusCode();
            var json = await response.Content.ReadAsStringAsync();
            var result = JsonSerializer.Deserialize<JsonElement>(json);
            int status = result.GetProperty("status").GetInt32();

            if (status == 2) return result; // Complete
            if (status == 3) throw new Exception(
                result.GetProperty("errorMessage").GetString());

            await Task.Delay(3000); // Poll every 3 seconds
        }
    }
}`;
  }

  getStatusVBNetCode(): string {
    if (!this.statusEndpoint || !this.token) return "'Endpoint or token not available";
    return `Imports System.Net.Http
Imports System.Text.Json

Module StatusChecker
    Private Const BearerToken As String = "${this.token}"
    Private Const StatusUrl As String = "${this.statusEndpoint}"

    ' Replace actionToken with the value returned from the start call
    ' Status codes: 0=Pending, 1=Running, 2=Complete, 3=Failed
    Public Async Function PollUntilCompleteAsync(actionToken As String) As Task(Of JsonElement)
        Using client As New HttpClient()
            client.DefaultRequestHeaders.Add("Authorization", $"Bearer {BearerToken}")

            Do While True
                Dim url As String = StatusUrl.Replace("{ACTION_TOKEN}", actionToken)
                Dim response = Await client.GetAsync(url)
                response.EnsureSuccessStatusCode()
                Dim json As String = Await response.Content.ReadAsStringAsync()
                Dim result As JsonElement = JsonSerializer.Deserialize(Of JsonElement)(json)

                ' Status: 0=Pending, 1=Running, 2=Complete, 3=Failed
                Dim statusCode As Integer = result.GetProperty("status").GetInt32()
                If statusCode = 2 Then Return result
                If statusCode = 3 Then
                    Throw New Exception(result.GetProperty("errorMessage").GetString())
                End If

                Await Task.Delay(3000) ' Poll every 3 seconds
            Loop
        End Using
    End Function
End Module`;
  }

  getStatusPythonCode(): string {
    if (!this.statusEndpoint || !this.token) return '# Endpoint or token not available';
    return `import requests
import time

BEARER_TOKEN = "${this.token}"
STATUS_URL = "${this.statusEndpoint}"

def poll_until_complete(action_token: str) -> dict:
    """
    Poll the status endpoint until the action is complete.
    Replace action_token with the value returned from the start call.
    Status codes: 0=Pending, 1=Running, 2=Complete, 3=Failed
    """
    headers = {"Authorization": f"Bearer {BEARER_TOKEN}"}
    url = STATUS_URL.replace("{ACTION_TOKEN}", action_token)

    while True:
        response = requests.get(url, headers=headers)
        response.raise_for_status()
        result = response.json()
        status = result["status"]

        if status == 2:  # Complete
            return result
        if status == 3:  # Failed
            raise Exception(f"Action failed: {result.get('errorMessage')}")

        time.sleep(3)  # Poll every 3 seconds`;
  }

  getStatusJavaCode(): string {
    if (!this.statusEndpoint || !this.token) return '// Endpoint or token not available';
    return `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import com.google.gson.JsonObject;
import com.google.gson.JsonParser;

public class StatusChecker {
    private static final String BEARER_TOKEN = "${this.token}";
    private static final String STATUS_URL = "${this.statusEndpoint}";
    private static final HttpClient client = HttpClient.newHttpClient();

    // Replace actionToken with the value returned from the start call
    // Status codes: 0=Pending, 1=Running, 2=Complete, 3=Failed
    public static JsonObject pollUntilComplete(String actionToken) throws Exception {
        String url = STATUS_URL.replace("{ACTION_TOKEN}", actionToken);
        while (true) {
            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(url))
                .header("Authorization", "Bearer " + BEARER_TOKEN)
                .GET()
                .build();
            HttpResponse<String> response = client.send(request,
                HttpResponse.BodyHandlers.ofString());
            JsonObject result = JsonParser.parseString(response.body()).getAsJsonObject();
            int status = result.get("status").getAsInt();
            if (status == 2) return result;
            if (status == 3) throw new RuntimeException(
                "Action failed: " + result.get("errorMessage").getAsString());
            Thread.sleep(3000); // Poll every 3 seconds
        }
    }
}`;
  }

  getResultsCurlCommand(): string {
    if (!this.resultsEndpoint || !this.token) return '# Endpoint or token not available';
    return `# Call this once the status endpoint returns status 2 (Complete)
# Replace {ACTION_TOKEN} with the token returned from the start call
curl -X GET "${this.resultsEndpoint}" \\
  -H "Authorization: Bearer ${this.token}"`;
  }

  getResultsCSharpCode(): string {
    if (!this.resultsEndpoint || !this.token) return '// Endpoint or token not available';
    return `using System.Net.Http;

class ResultsFetcher
{
    private static readonly string BearerToken = "${this.token}";
    private static readonly string ResultsUrl = "${this.resultsEndpoint}";

    // Call this once the status endpoint returns status 2 (Complete)
    // Replace actionToken with the value returned from the start call
    public static async Task<string> GetResultAsync(string actionToken)
    {
        using var client = new HttpClient();
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {BearerToken}");
        var url = ResultsUrl.Replace("{ACTION_TOKEN}", actionToken);
        var response = await client.GetAsync(url);
        response.EnsureSuccessStatusCode();
        return await response.Content.ReadAsStringAsync();
    }
}`;
  }

  getResultsVBNetCode(): string {
    if (!this.resultsEndpoint || !this.token) return "'Endpoint or token not available";
    return `Imports System.Net.Http

Module ResultsFetcher
    Private Const BearerToken As String = "${this.token}"
    Private Const ResultsUrl As String = "${this.resultsEndpoint}"

    ' Call this once the status endpoint returns status 2 (Complete)
    ' Replace actionToken with the value returned from the start call
    Public Async Function GetResultAsync(actionToken As String) As Task(Of String)
        Using client As New HttpClient()
            client.DefaultRequestHeaders.Add("Authorization", $"Bearer {BearerToken}")
            Dim url As String = ResultsUrl.Replace("{ACTION_TOKEN}", actionToken)
            Dim response = Await client.GetAsync(url)
            response.EnsureSuccessStatusCode()
            Return Await response.Content.ReadAsStringAsync()
        End Using
    End Function
End Module`;
  }

  getResultsPythonCode(): string {
    if (!this.resultsEndpoint || !this.token) return '# Endpoint or token not available';
    return `import requests

BEARER_TOKEN = "${this.token}"
RESULTS_URL = "${this.resultsEndpoint}"

def get_result(action_token: str) -> dict:
    """
    Fetch the results once the status endpoint returns status 2 (Complete).
    Replace action_token with the value returned from the start call.
    """
    headers = {"Authorization": f"Bearer {BEARER_TOKEN}"}
    url = RESULTS_URL.replace("{ACTION_TOKEN}", action_token)
    response = requests.get(url, headers=headers)
    response.raise_for_status()
    return response.json()`;
  }

  getResultsJavaCode(): string {
    if (!this.resultsEndpoint || !this.token) return '// Endpoint or token not available';
    return `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;

public class ResultsFetcher {
    private static final String BEARER_TOKEN = "${this.token}";
    private static final String RESULTS_URL = "${this.resultsEndpoint}";
    private static final HttpClient client = HttpClient.newHttpClient();

    // Call this once the status endpoint returns status 2 (Complete)
    // Replace actionToken with the value returned from the start call
    public static String getResult(String actionToken) throws Exception {
        String url = RESULTS_URL.replace("{ACTION_TOKEN}", actionToken);
        HttpRequest request = HttpRequest.newBuilder()
            .uri(URI.create(url))
            .header("Authorization", "Bearer " + BEARER_TOKEN)
            .GET()
            .build();
        HttpResponse<String> response = client.send(request,
            HttpResponse.BodyHandlers.ofString());
        if (response.statusCode() != 200) {
            throw new RuntimeException("API returned status: " + response.statusCode());
        }
        return response.body();
    }
}`;
  }

  selectCodeText(event: MouseEvent): void {
    const preElement = event.target as HTMLPreElement;
    const range = document.createRange();
    range.selectNodeContents(preElement);
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  getDataSourceTypeName(): string {
    if (!this.data?.dataSource) return 'Unknown';
    switch (this.data.dataSource.dataSourceTypeId) {
      case 1: return 'API Listener';
      case 2: return 'Excel File';
      case 3: return 'CSV File';
      case 4: return 'Parquet File';
      case 5: return 'JSON File';
      default: return 'Unknown';
    }
  }
}
